import axios, { AxiosInstance } from 'axios';
import {
  LoginResponse,
  User,
  SummaryResponse,
  TopAuthorsResponse,
  TopUsersResponse,
  AllAuthorsResponse,
  AllBooksResponse,
  AllUsersResponse,
  BookViewResponse,
  BooksListResponse,
  EditionsListResponse,
  AppUsersResponse,
  ExcludedUsersResponse,
  ApiKeyResponse,
  SyncResponse,
  SyncHistoryResponse,
  AppUser,
  ExcludedUser,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', {
      username,
      password,
    });
    return response.data;
  }

  async getMe(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  // Analytics
  async getSummary(from?: string, to?: string): Promise<SummaryResponse> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<SummaryResponse>(
      `/analytics/summary${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getTopAuthors(limit?: number, from?: string, to?: string): Promise<TopAuthorsResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<TopAuthorsResponse>(
      `/analytics/top-authors${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getTopUsers(limit?: number, from?: string, to?: string): Promise<TopUsersResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<TopUsersResponse>(
      `/analytics/top-users${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getViewsByBook(
    book: string,
    edition?: string,
    from?: string,
    to?: string
  ): Promise<BookViewResponse> {
    const params = new URLSearchParams();
    params.append('book', book);
    if (edition) params.append('edition', edition);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<BookViewResponse>(
      `/analytics/by-book?${params.toString()}`
    );
    return response.data;
  }

  async getAllAuthors(from?: string, to?: string): Promise<AllAuthorsResponse> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<AllAuthorsResponse>(
      `/analytics/all-authors${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getAllBooks(from?: string, to?: string): Promise<AllBooksResponse> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<AllBooksResponse>(
      `/analytics/all-books${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getAllUsers(from?: string, to?: string): Promise<AllUsersResponse> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const response = await this.client.get<AllUsersResponse>(
      `/analytics/all-users${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getBooksList(): Promise<BooksListResponse> {
    const response = await this.client.get<BooksListResponse>('/analytics/books-list');
    return response.data;
  }

  async getEditionsList(book?: string): Promise<EditionsListResponse> {
    const params = book ? `?book=${encodeURIComponent(book)}` : '';
    const response = await this.client.get<EditionsListResponse>(
      `/analytics/editions-list${params}`
    );
    return response.data;
  }

  // Admin - Users
  async getAppUsers(): Promise<AppUsersResponse> {
    const response = await this.client.get<AppUsersResponse>('/admin/users');
    return response.data;
  }

  async createAppUser(
    username: string,
    password: string,
    role: 'analyst' | 'admin'
  ): Promise<{ user: AppUser }> {
    const response = await this.client.post<{ user: AppUser }>('/admin/users', {
      username,
      password,
      role,
    });
    return response.data;
  }

  async deleteAppUser(userId: number): Promise<void> {
    await this.client.delete(`/admin/users/${userId}`);
  }

  // Admin - API Key
  async getApiKey(): Promise<ApiKeyResponse> {
    const response = await this.client.get<ApiKeyResponse>('/admin/settings/api-key');
    return response.data;
  }

  async setApiKey(apiKey: string): Promise<void> {
    await this.client.put('/admin/settings/api-key', { apiKey });
  }

  // Admin - Excluded Users
  async getExcludedUsers(): Promise<ExcludedUsersResponse> {
    const response = await this.client.get<ExcludedUsersResponse>('/admin/excluded-users');
    return response.data;
  }

  async addExcludedUser(pattern: string): Promise<{ excludedUser: ExcludedUser }> {
    const response = await this.client.post<{ excludedUser: ExcludedUser }>(
      '/admin/excluded-users',
      { pattern }
    );
    return response.data;
  }

  async deleteExcludedUser(patternId: number): Promise<void> {
    await this.client.delete(`/admin/excluded-users/${patternId}`);
  }

  // Sync
  async triggerSync(from?: string, to?: string): Promise<SyncResponse> {
    const response = await this.client.post<SyncResponse>('/sync/trigger', { from, to });
    return response.data;
  }

  async getSyncHistory(): Promise<SyncHistoryResponse> {
    const response = await this.client.get<SyncHistoryResponse>('/admin/sync-history');
    return response.data;
  }
}

export const api = new ApiService();
